// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { connectRouteTo } from 'redux-router-kit';
import { type HOC, compose } from 'recompose';

import { graphql } from '@apollo/react-hoc';

import * as Account from 'app/entities/Account';
import DashboardActions from 'app/dashboard/DashboardActions';
import HeldTasksBanner from 'app/dashboard/components/HeldTasksBanner';
import TaskHistoryActions from 'app/history/TaskHistoryActions';
import servicesByIdsQuery from 'app/graphql/queries/service/servicesByIds';
import shouldComponentUpdate from 'app/perf-utils/components/shouldComponentUpdate';
import { HELD_STATUSES_URL } from 'app/dashboard/utils/constants';
import { HeldReasons } from 'app/history/utils/constants';
import { enableSentryRequestLogging } from 'app/common/middlewares/sentryErrorsMiddleware';
import { getCurrentPathname } from 'app/common/utils/RouterSelectors';
import { selectServicesMap } from 'app/service/Selectors';
import {
  getActionableStepsPropsList,
  getHeldTaskBannerProps,
  hasDifferencesFromSnapshot,
} from 'app/dashboard/utils/HeldTaskBannerUtils';
import {
  selectAreAllStartsExpiringOnTheSameDay,
  selectCountsByHeldReason,
  selectHeldTaskStaleAuthServiceNames,
  selectHeldTasksSnapshot,
  selectHeldTasksStartsExpiringOn,
  selectIsHeldTasksBannerOpen,
  selectTotalHeldTasks,
} from 'app/dashboard/Selectors';

import type { ActionableHeldTasksProps } from 'app/dashboard/utils/HeldTaskBannerUtils';
import type { Dispatch } from 'app/common/types';

type Props = {
  inUseServices: Array<string>,
};

type ContainerProps = {
  isLoading: boolean,
  totalHeldTaskCount: ?number,
  accountId: number,
  isOpen: boolean,
  title: string,
  buttonText: string,
  daysRemainText: string,
  msg: string,
  stepsPropsList: ActionableHeldTasksProps[],
  numOfStepsDone: number,
  shouldCreateNewSnapshot: boolean,
  currentPathname: string,
  loadData: () => void,
  loadHeldReasons: () => void,
  onClick: () => void,
  onReplayHeldTasks: () => void,
  createSnapshot: () => void,
};

class HeldTasksBannerStateful extends Component<Props & ContainerProps> {
  shouldComponentUpdate = shouldComponentUpdate.deepCheckProps;

  componentDidMount() {
    const { accountId, loadData } = this.props;
    if (accountId) {
      loadData();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: ContainerProps) {
    const {
      accountId,
      currentPathname,
      shouldCreateNewSnapshot,
      loadData,
      loadHeldReasons,
      createSnapshot,
    } = this.props;

    // For some pages such as '/apps/integrations', it takes longer to fetch account data
    if (!accountId && nextProps.accountId) {
      loadData();
    }

    if (!shouldCreateNewSnapshot && nextProps.shouldCreateNewSnapshot) {
      createSnapshot();
    }

    if (
      currentPathname &&
      nextProps.currentPathname &&
      currentPathname !== nextProps.currentPathname
    ) {
      loadHeldReasons();
    }
  }

  render() {
    const { totalHeldTaskCount, isLoading } = this.props;

    if (!totalHeldTaskCount || isLoading) {
      return null;
    }

    return <HeldTasksBanner {...this.props} />;
  }
}

const mapDispatchToProps = (dispatch: Dispatch, props) => {
  const services = selectServicesMap(null, props);

  return {
    loadData() {
      dispatch(DashboardActions.loadAllHeldTaskData());
    },
    loadHeldReasons() {
      dispatch(DashboardActions.loadHeldReasons());
    },
    onClick() {
      dispatch(
        DashboardActions.toggleHeldTasksBannerAndCreateSnapshot(services)
      );
    },
    createSnapshot() {
      dispatch(DashboardActions.createSnapshot(services));
    },
    onReplayHeldTasks() {
      dispatch(TaskHistoryActions.routeToThenSelectAll(HELD_STATUSES_URL));
    },
  };
};

const mapStateToProps = (state, ownProps) => {
  const services = selectServicesMap(null, ownProps);

  const staleAuthServiceNames = selectHeldTaskStaleAuthServiceNames(
    state,
    services
  );

  const isOpen = selectIsHeldTasksBannerOpen(state);
  const countsByHeldReason = selectCountsByHeldReason(state);
  const rawCountsByHeldReason = selectCountsByHeldReason(state, {
    noAdjustments: true,
  });
  const totalHeldTaskCount = selectTotalHeldTasks(state);
  const heldTasksStartsExpiringOn = selectHeldTasksStartsExpiringOn(state);
  const sameExpirationDate = selectAreAllStartsExpiringOnTheSameDay(state);
  const snapshot = selectHeldTasksSnapshot(state);

  const hasDiff = hasDifferencesFromSnapshot(
    {
      countsByHeldReason,
      staleAuthServiceNames,
      totalHeldTaskCount,
    },
    snapshot
  );

  const actionableStepsPropsList = getActionableStepsPropsList({
    countsByHeldReason,
    rawCountsByHeldReason,
    staleAuthServiceNames,
    totalHeldTaskCount,
    snapshot,
  });

  const numOfStepsDone = actionableStepsPropsList.filter(step => step.isDone)
    .length;
  const numOfStepsLeft = actionableStepsPropsList.length - numOfStepsDone;

  const bannerProps = getHeldTaskBannerProps({
    heldTasksStartsExpiringOn,
    totalHeldTaskCount,
    sameExpirationDate,
    numOfStepsDone,
    numOfStepsLeft,
  });

  // 'Cuz it takes a few seconds to fetch services data
  const isLoading =
    (countsByHeldReason[HeldReasons.STALE_AUTH] && _.isEmpty(services)) ||
    _.isEmpty(bannerProps);

  return {
    isLoading,
    accountId: Account.selectors.all.currentAccountId(state),
    totalHeldTaskCount,
    isOpen,
    ...bannerProps,
    stepsPropsList: actionableStepsPropsList,
    numOfStepsDone,
    shouldCreateNewSnapshot: hasDiff,
    currentPathname: getCurrentPathname(state),
  };
};

const withData = graphql(servicesByIdsQuery, {
  skip: props => _.isEmpty(props.inUseServices),
  options: props => ({
    variables: enableSentryRequestLogging({
      ids: props.inUseServices,
    }),
  }),
});

const enhance: HOC<*, {}> = compose(
  withData,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  connectRouteTo
);

export default enhance(HeldTasksBannerStateful);
